import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import TableModal from '../../../../../common/Modal/TableModal/TableModal';

// PHOB-1710: render an activation date cell — "In Progress" while the segment is still being
// created asynchronously (activation.<type>.status === 'in_progress'), otherwise the activated
// date formatted like the other date columns. Used for both amplified and always-on columns.
/* eslint-disable react/prop-types */
const makeActivationDateCell = (type, dateField) => (row) => {
  if (row.activation && row.activation[type] && row.activation[type].status === 'in_progress') {
    return 'In Progress';
  }
  if (!row[dateField]) {
    return '';
  }
  return new Date(row[dateField]).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
const formatAmplifiedDateCell = makeActivationDateCell('amplified', 'dateAmplified');
const formatAlwaysOnDateCell = makeActivationDateCell('always_on', 'dateAlwaysOn');
/* eslint-enable react/prop-types */

// PHOB-1857: for the unified TV source, "Date Activated" represents *any* activation the audience
// went through. Two shapes carry that date: the per-type activation sub-docs (a segment activated
// through the single-source flows), and the flat is_activated/activated_at pair that
// activate_unified stamps on the unified parent (its per-source activation sub-docs live on the
// generated child audiences, not on the parent). Collect both and show the earliest.
const ACTIVATION_TYPES = ['deterministic', 'amplified', 'always_on'];

const earliestActivationDate = (audience) => {
  const dates = ACTIVATION_TYPES.map((type) => audience.activation?.[type]?.activated_date)
    .concat(audience.activated_at)
    .filter(Boolean);
  return dates.length ? dates.reduce((min, date) => (new Date(date) < new Date(min) ? date : min)) : undefined;
};

/* eslint-disable react/prop-types */
const formatUnifiedActivationDateCell = (row) => {
  const earliest = earliestActivationDate(row);
  if (earliest) {
    return new Date(earliest).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  if (ACTIVATION_TYPES.some((type) => row.activation?.[type]?.status === 'in_progress')) {
    return 'In Progress';
  }
  // Activated, but the backend left no timestamp — still better than an empty cell, which reads
  // as "never activated".
  return row.is_activated ? 'Activated' : '';
};
/* eslint-enable react/prop-types */

const LoadAudienceModal = ({
  data = [],
  onSubmit,
  onCancel,
  onDelete,
  isOpen = false,
  channel,
  isActivateAudienceVisible = false,
  userPermissions = [],
}) => {
  // Check if user has specific permission
  const hasPermission = (permission) => userPermissions.includes(permission);

  // Transform data to add display fields
  const transformedData = useMemo(() => {
    const filteredData = channel
      ? data.filter((audience) => audience.channel === channel || (channel === 'articles' && !audience.channel))
      : data;
    return filteredData.map((audience) => ({
      ...audience,
      createdBy: audience.created_by,
      dateActivated: audience.activation?.deterministic?.activated_date,
      dateAmplified: audience.activation?.amplified?.activated_date,
      dateAlwaysOn: audience.activation?.always_on?.activated_date,
      dateActivatedAny: earliestActivationDate(audience),
      createdAt: audience.created_at,
    }));
  }, [data, channel]);

  const handleDelete = (audience, event) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(audience);
    }
  };

  const DeleteButton = ({ row }) => (
    <span className="action-btn delete-btn" onClick={(e) => handleDelete(row, e)} title="Delete">
      <i className="icon-trash pointer"></i>
    </span>
  );

  DeleteButton.displayName = 'DeleteButton';
  DeleteButton.propTypes = {
    row: PropTypes.object.isRequired,
  };

  const renderDeleteButton = (row) => <DeleteButton row={row} />;
  renderDeleteButton.displayName = 'renderDeleteButton';

  // Dynamic column configuration based on original Angular logic
  const columns = useMemo(() => {
    // Base columns (always shown)
    const baseColumns = [
      {
        id: 'name',
        title: 'Audience Name',
        sortable: true,
        className: 'cell-name',
        showTooltip: true,
        minWidth: '200px',
        // Drives a grid track of minmax(200px, 50%): the primary column grows toward
        // half the table width (matching the 50% search bar), capped there and
        // truncated with an ellipsis past it. Created By (1fr) takes only the residual.
        maxWidth: '50%',
      },
      {
        id: 'createdBy',
        title: 'Created By',
        sortable: true,
        className: 'cell-name',
        showTooltip: true,
        // 1fr: absorbs the spare width left by the content-sized name column. minWidth
        // fits the header + sort icon so it never truncates when space is tight.
        minWidth: '150px',
        flex: 1,
      },
      {
        id: 'createdAt',
        title: 'Date Added',
        sortable: true,
        className: 'cell-date',
        minWidth: '140px',
      },
    ];

    // Build activation fields based on original logic
    let activationFields = [];

    if (channel === 'unified_tv') {
      // PHOB-1857: for unified TV, isActivateAudienceVisible is rolled up per-source from the segment
      // currently sitting in the builder, so it's false for a fresh or US-only segment. Whether this
      // column appears must not depend on the builder's contents — fall back to the TV activation
      // permission, which is what per-source visibility reduces to anyway.
      if (isActivateAudienceVisible || hasPermission('tv activation')) {
        activationFields = [
          {
            id: 'dateActivatedAny',
            title: 'Date Activated',
            sortable: true,
            className: 'cell-date',
            minWidth: '130px',
            template: formatUnifiedActivationDateCell,
          },
        ];
      }
    } else if (isActivateAudienceVisible) {
      // Always include date_amplified
      activationFields.push({
        id: 'dateAmplified',
        title: 'Date Amplified',
        sortable: true,
        className: 'cell-date',
        minWidth: '140px',
        template: formatAmplifiedDateCell,
      });

      // Add date_always_on if user has permission
      if (hasPermission('audience activation - always on')) {
        activationFields.push({
          id: 'dateAlwaysOn',
          title: 'Date Always On Created',
          sortable: true,
          className: 'cell-date',
          minWidth: '180px',
          template: formatAlwaysOnDateCell,
        });
      }

      // Add date_activated at the beginning if channel !== 'articles'
      if (channel !== 'articles') {
        activationFields = [
          {
            id: 'dateActivated',
            title: 'Date Activated',
            sortable: true,
            className: 'cell-date',
            minWidth: '130px',
          },
          ...activationFields,
        ];
      }
    }

    // Actions column (always at the end)
    const actionsColumn = {
      id: 'actions',
      title: '',
      sortable: false,
      className: 'cell-actions',
      width: '56px',
      template: renderDeleteButton,
    };

    // Combine all columns
    return [...baseColumns, ...activationFields, actionsColumn];
  }, [channel, isActivateAudienceVisible, userPermissions]);

  const handleRowClick = (audience) => {
    if (onSubmit) {
      onSubmit(audience);
    }
  };

  const width = useMemo(() => {
    // Use responsive viewport-based width that adjusts based on features
    let baseWidth = '80vw';
    let maxWidth = '1400px';
    let minWidth = '900px';

    // Return CSS clamp for responsive sizing
    return `clamp(${minWidth}, ${baseWidth}, ${maxWidth})`;
  }, [channel, isActivateAudienceVisible, userPermissions]);

  return (
    <TableModal
      isOpen={isOpen}
      modalTitle="Load a Program Audience"
      subtitle="Select an audience by clicking its row"
      data={transformedData}
      columns={columns}
      onRowClick={handleRowClick}
      onCancel={onCancel}
      searchPlaceholder="Search audiences..."
      emptyMessage="No audiences found"
      searchableFields={['name', 'createdBy']}
      defaultSort={{ key: 'createdAt', direction: 'desc' }}
      className="load-audience-modal"
      width={width}
      height="527px"
    />
  );
};

LoadAudienceModal.propTypes = {
  data: PropTypes.array,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  isOpen: PropTypes.bool,
  channel: PropTypes.string,
  isActivateAudienceVisible: PropTypes.bool,
  userPermissions: PropTypes.array,
};

export default LoadAudienceModal;
